import queryString from 'query-string';
import { getArray, getCheckedCheckboxes } from './helpers';
import { addDays, format, isValid, parse } from 'date-fns';
import { pushEvent } from './analytics';
import { GET_PAGE_FROM_URL, ENTRY_FLOW_INIT } from '../constants/analyticEvents';
// import { sendSearchPerformed } from '../utilities/sendSearchPerformed';

const isValidDate = (dateString, formatString) => {
  const parsedDate = parse(dateString, formatString, new Date());
  return isValid(parsedDate) && dateString === format(parsedDate, formatString);
};

export const getFormattedWhen = (parsedData) => {
  const data = {
    type: ['month', 'range', 'specific'].includes(parsedData.type || parsedData.pr_type) ? (parsedData.type || parsedData.pr_type) : 'month',
    maxNights: parsedData.maxNights,
    minNights: parsedData.minNights,
    weekendsOnly: ((isEntryFlow() || isEntryFlow('link-presets-welcome')) && parsedData.pr_weekendsOnly) ? parsedData.pr_weekendsOnly : parsedData.weekendsOnly,
    months: (isEntryFlow('any') && parsedData.pr_months) ? getArray(parsedData.pr_months) : getArray(parsedData.months),
    nights: (isEntryFlow('any') && parsedData.pr_nights) ? getArray(parsedData.pr_nights) : getArray(parsedData.nights),
  };

  if (parsedData.nextdays) {
    const nextDays = parseInt(parsedData.nextdays, 10);
    if (isNaN(nextDays) || nextDays <= 0) {
      console.error('Invalid nextdays value:', parsedData.nextdays);
      return null;
    }
    const startDate = addDays(new Date(), 1);
    const endDate = addDays(startDate, nextDays);

    return {
      ...data,
      type: 'range',
      startDate: format(startDate, 'dd/MM/yyyy'),
      endDate: format(endDate, 'dd/MM/yyyy'),
    };
  }

  const startDateString = parsedData.startDate || parsedData.pr_startDate;
  const endDateString = parsedData.endDate || parsedData.pr_endDate;

  return {
    ...data,
    startDate: startDateString && isValidDate(startDateString, 'dd-MM-yyyy')
      ? format(parse(startDateString, 'dd-MM-yyyy', new Date()), 'dd/MM/yyyy')
      : null,
    endDate: endDateString && isValidDate(endDateString, 'dd-MM-yyyy')
      ? format(parse(endDateString, 'dd-MM-yyyy', new Date()), 'dd/MM/yyyy')
      : null,
  };
};

export function getDataFromUrl(type) {
  const parsedData = getParamsFromUrl();
  if (type && type === 'mainFilter' && parsedData && Object.keys(parsedData).length > 0 && (isEntryFlow('link-presets') || isEntryFlow('link-presets-welcome'))) {
    pushEvent(ENTRY_FLOW_INIT, { url: window.location.href, prParams: parsedData });
  }
  let pr_childAges = [];
  if (isEntryFlow('any') && parsedData.pr_children) {
    const { childAges } = prParamsToUrl(false);
    pr_childAges = [...childAges];
  }

  const mainFilter = {
    what: (isEntryFlow('any') && parsedData.pr_what) ? getArray(parsedData.pr_what) : getArray(parsedData.what),
    where: {
      destinations: (isEntryFlow('any') && parsedData.pr_destinations) ? getArray(parsedData.pr_destinations) : getArray(parsedData.destinations),
      destinationsGroups: (isEntryFlow('any') && parsedData.pr_destinationsGroups) ? getArray(parsedData.pr_destinationsGroups) : getArray(parsedData.destinationsGroups),
    },
    when: getFormattedWhen(parsedData),
    who: {
      adults: (isEntryFlow('any') && parsedData.pr_adults) ? parsedData.pr_adults : parsedData?.adults,
      children: (isEntryFlow('any') && parsedData.pr_children) ? parsedData.pr_children : parsedData?.children,
      babies: (isEntryFlow('any') && parsedData.pr_babies) ? parsedData.pr_babies : parsedData?.babies,
      rooms: parsedData?.rooms,
      childAges: (isEntryFlow('any') && pr_childAges.length) ? pr_childAges : getArray(parsedData?.childAges),
    },
    budget: parsedData?.budget,
    fromwhere: (isEntryFlow('any') && parsedData.pr_fromwhere) ? getArray(parsedData.pr_fromwhere) : (getArray(parsedData?.fromwhere) || []),
    atlasTop: parsedData?.atlasTop || '',
  };

  const secondaryFilter = {
    stars: getArray(parsedData.stars),
    rating: getArray(parsedData.rating),
    board: getArray(parsedData.board),
    stops: getArray(parsedData.stops),
    facilities: getArray(parsedData.facilities),
    duration: parsedData.duration,
    freeCancellation: parsedData.freeCancellation === 'true',
    sort: parsedData.sort || 'best',
    order: parsedData.order || 1,
    luggage: getArray(parsedData.luggage),
    flex: parsedData.flex || false,
  };

  const search = {
    selectedHotel: {
      hotelId: (isEntryFlow('any') && parsedData.pr_hotelId) ? parsedData.pr_hotelId : parsedData.hotelId,
      hfHotelId: parsedData.hfHotelId,
    }
  };

  if (type && type === 'mainFilter' && parsedData && Object.keys(parsedData).length > 0 && (typeof parsedData.step === 'undefined' || (typeof parsedData.utm_campaign !== 'undefined' && parsedData.utm_campaign === 'share_link') || isEntryFlow('offer-results')) && !isEntryFlow('link-presets') && !isEntryFlow('link-presets-welcome')) {
    const moreIn = parsedData.moreIn;
    const origin = parsedData.origin;
    setTimeout(() => {
      pushEvent(GET_PAGE_FROM_URL, { moreIn, origin, parsedData });
      // sendSearchPerformed(); // it's moved to load offers event
    }, 500);
  }

  return {
    mainFilter,
    secondaryFilter,
    search,
  };
}

export function setDataToUrl({ mainFilter, secondaryFilter, search }) {
  const {
    what = [],
    where = [],
    when = {},
    budget,
    who = {},
    fromwhere,
  } = mainFilter;
  const {
    sort,
    duration,
    facilities,
    stops,
    board,
    rating,
    stars,
    freeCancellation,
    luggage,
    flex,
  } = secondaryFilter;
  const { initial: initialWhenData, weekendsOnly, ...whenData } = when;
  const { initial: initialWhoData, ...whoData } = who;

  const dateParams = {
    ...whenData,
    startDate: when.startDate
      ? format(parse(when.startDate, 'dd/MM/yyyy', new Date()), 'dd-MM-yyyy')
      : undefined,
    endDate: when.endDate
      ? format(parse(when.endDate, 'dd/MM/yyyy', new Date()), 'dd-MM-yyyy')
      : undefined,
    minNights: whenData.minNights > 0 ? whenData.minNights : undefined,
    maxNights: whenData.maxNights > 0 ? whenData.maxNights : undefined,
    nights: whenData.nights,
  };

  let mainFilterParams = {
    what: getCheckedCheckboxes(what),
    destinationsGroups: getCheckedCheckboxes(where.destinationsGroups),
    destinations: getCheckedCheckboxes(where.destinations),
    budget: [budget?.min, budget?.max],
    ...dateParams,
    ...whoData,
    fromwhere: getCheckedCheckboxes(fromwhere),
  };

  if (weekendsOnly) {
    mainFilterParams.weekendsOnly = true;
  }

  const secondaryFilterParams = {
    stars: stars.filter((item) => item.active).map((item) => item.value),
    rating: rating.filter((item) => item.active).map((item) => item.value),
    board: board.filter((item) => item.active).map((item) => item.value),
    stops: stops.filter((item) => item.active).map((item) => item.value),
    facilities: facilities
      .filter((item) => item.active)
      .map((item) => item.value),
    duration: duration.activeTime,
    sort: sort.find((item) => item.active).label,
    order: sort.find((item) => item.active).value,
    luggage: luggage && luggage.filter((item) => item.active).map((item) => item.value),
  };

  if (freeCancellation) {
    secondaryFilterParams.freeCancellation = true;
  }
  if (flex && flex.active) {
    secondaryFilterParams.flex = true;
  }

  // Search params
  const searchParams = {
    hotelId: search.hotelId,
    hfHotelId: search.hfHotelId,
  };

  const parsedData = getParamsFromUrl();
  let otherParams = {
    pr_entryFlow: parsedData.pr_entryFlow,
    mainDestinationId: parsedData.mainDestinationId,
  };
  if (isEntryFlow('link-presets') || isEntryFlow('link-presets-welcome')) {
    otherParams = {
      pr_entryFlow: parsedData.pr_entryFlow,
      pr_name: parsedData.pr_name,
      pr_messageId: parsedData.pr_messageId,
      pr_what: parsedData.pr_what,
      pr_destinations: parsedData.pr_destinations,
      pr_destinationsGroups: parsedData.pr_destinationsGroups,
      pr_type: parsedData.pr_type,
      pr_months: parsedData.pr_months,
      pr_startDate: parsedData.pr_startDate,
      pr_endDate: parsedData.pr_endDate,
      pr_nights: parsedData.pr_nights,
      pr_capacity: parsedData.pr_capacity,
      pr_hostingLevelPref: parsedData.pr_hostingLevelPref,
      pr_weekendsOnly: parsedData.pr_weekendsOnly,
      pr_hotelId: parsedData.pr_hotelId,
      pr_offerId: parsedData.pr_offerId,
      pr_similar_dest: parsedData.pr_similar_dest,
      pr_fromwhere: parsedData.pr_fromwhere,
      pr_backgroundImg: parsedData.pr_backgroundImg,
    };
  }
  if (isEntryFlow('offer-results')) {
    /* don't remove for now, I use it for testing
    otherParams = {
      pr_entryFlow: parsedData.pr_entryFlow,
      pr_startDate: parsedData.pr_startDate,
      pr_endDate: parsedData.pr_endDate,
      pr_hotelId: parsedData.pr_hotelId,
      pr_offerId: parsedData.pr_offerId,
      pr_adults: parsedData.pr_adults,
      pr_children: parsedData.pr_children,
      pr_bigChildren: parsedData.pr_bigChildren,
      pr_infant: parsedData.pr_infant,
    }; */
    const prParams = prParamsToUrl(false);
    if (prParams) {
      mainFilterParams = {
        ...mainFilterParams,
        ...prParams,
      }
    }
    if (parsedData.pr_hotelId) {
      searchParams.hotelId = parsedData.pr_hotelId;
    }
    pushEvent(ENTRY_FLOW_INIT, { url: window.location.href, prParams: parsedData });
  }

  const params = {
    ...mainFilterParams,
    ...secondaryFilterParams,
    ...searchParams,
    ...otherParams,
  };

  window.location.hash = queryString.stringify(params, {
    arrayFormat: 'comma',
  });
  if (isEntryFlow('offer-results')) {
    removeParamFromUrl(['pr_entryFlow']);
  }
}

export function getParamFromUrl(param) {
  const parsedData = queryString.parse(window.location.hash, {
    parseNumbers: true,
    arrayFormat: 'comma',
    parseBooleans: true,
  });

  return parsedData[param];
}

export function getParamsFromUrl() {
  return queryString.parse(window.location.hash, {
    parseNumbers: true,
    arrayFormat: 'comma',
    parseBooleans: true,
  });
}

export function convertParamsToHash(params) {
  return queryString.stringify(params, {
    arrayFormat: 'comma',
  });
}

export function setParamToUrl(param) {
  const currentParams = getParamsFromUrl();

  window.location.hash = queryString.stringify(
    {
      ...currentParams,
      ...param,
    },
    {
      arrayFormat: 'comma',
    }
  );
}

export function removeParamFromUrl(param) {
  // param should be an array of url hash parameters names to remove, like ['param1', 'param2']
  if (!(param && param.length)) { return false; }
  const currentParams = getParamsFromUrl();
  param.forEach((paramToRemove) => {
    if (typeof currentParams[paramToRemove] !== 'undefined') {
      delete currentParams[paramToRemove];
    }
  });
  window.location.hash = queryString.stringify(currentParams, {
    arrayFormat: 'comma',
  });
}

export function prParamsToUrl(isRedirect) {
  // change pr_ params in the url to "regular" url params or just return params
  if (!isEntryFlow('any')) { return false; }
  const currentParams = getParamsFromUrl();
  const hfLastFilters = JSON.parse(localStorage.getItem('hfLastFilters'));
  const newParams = {};
  const childAges = [];
  let bigChildNum = 0;
  let babiesNum = 0;
  Object.keys(currentParams).forEach((param) => {
    if (param !== 'pr_entryFlow' && param !== 'pr_messageId' && param !== 'pr_name' && param !== 'pr_similar_dest') {
      if (param.indexOf('pr_') > -1) {
        if (param === 'pr_bigChildren') {
          // fill 17yo to childAge for older children
          bigChildNum = currentParams[param];
          const bigChild = new Array(currentParams[param]).fill(17);
          childAges.push(...bigChild);
        } else if (param === 'pr_infant') {
          // fill 1yo to childAge for babies
          babiesNum = currentParams[param];
          const babyChild = new Array(currentParams[param]).fill(1);
          childAges.push(...babyChild);
        } else {
          const newKey = param.replace("pr_", "");
          newParams[newKey] = currentParams[param];
        }
      } else {
        newParams[param] = currentParams[param];
      }
    }
  });
  const regularChildNum = newParams['children'] - childAges.length;
  if (newParams['children'] && regularChildNum > 0) {
    // fill 10yo to childAge for "normal" children
    const regChild = new Array(regularChildNum).fill(10);
    childAges.push(...regChild);
  }
  if (childAges.length) {
    newParams.childAges = childAges;
  }
  // compare also number of big children and babies with saved child ages
  let bigChildNumSaved = 0;
  let babiesNumSaved = 0;
  if (hfLastFilters && hfLastFilters?.engine?.who?.childAges && hfLastFilters?.engine?.who?.childAges?.length) {
    bigChildNumSaved = hfLastFilters.engine.who.childAges.filter(child => child > 11).length || 0;
    babiesNumSaved = hfLastFilters.engine.who.childAges.filter(child => child < 2).length || 0;
  }
  if (hfLastFilters && hfLastFilters?.engine?.who?.adult &&
    (isRedirect ||
      (newParams.adults && newParams.children &&
        +newParams.adults === hfLastFilters.engine.who.adult &&
        +newParams.children === hfLastFilters.engine.who.child &&
        bigChildNum === bigChildNumSaved &&
        babiesNum === babiesNumSaved
      )
    )
  ) {
    newParams.adults = hfLastFilters.engine.who.adult;
    newParams.children = hfLastFilters.engine.who.child;
    if (hfLastFilters.engine.who.childAges && hfLastFilters.engine.who.childAges.length) {
      newParams.childAges = hfLastFilters.engine.who.childAges;
    }
  }
  if (isRedirect) {
    window.location.hash = queryString.stringify(newParams, {
      arrayFormat: 'comma',
    });
  } else {
    return newParams;
  }
}

export function isEntryFlow(type='link-presets') {
  // check if url has pr_entryFlow and it's equal to link-presets (or other type value)
  // this is instead of checking pr_active=true (the same functionality)
  if (type === 'any') {
    return !!(getParamFromUrl('pr_entryFlow'));
  }
  return !!(getParamFromUrl('pr_entryFlow') && getParamFromUrl('pr_entryFlow') === type);
}
